/**
 * 前端版 prompt 拼装：跟后端 keyframeService.buildKeyframePrompt / buildVideoPrompt 等价。
 *
 * 用于 ShotInspector 的「Prompt 预览」面板，让用户看到镜头调度 + @ 提及最终拼成什么文本。
 *
 * 真实生成时仍由后端拼装（前端预览仅作可视化），所以此处的逻辑必须跟后端保持一致——
 * 后端常量改了，这里也要改。
 *
 * 编号顺序（与后端一致）：[...角色, ...物品, 场景]
 *
 * 注：自 2026-05-30 起统一 @ 语法（角色 / 物品 / 场景 共用 @），# 不再支持。
 */

const CAMERA_LABELS: Record<string, { zh: string; en: string }> = {
  static: { zh: '镜头不动', en: 'static camera / locked shot' },
  push: { zh: '推镜', en: 'push-in / dolly in' },
  pull: { zh: '拉镜', en: 'pull-out / dolly out' },
  pan_left: { zh: '左摇', en: 'pan left' },
  pan_right: { zh: '右摇', en: 'pan right' },
  tilt_up: { zh: '上摇', en: 'tilt up' },
  tilt_down: { zh: '下摇', en: 'tilt down' },
  orbit: { zh: '环绕', en: 'orbit / arc shot' },
};

const INTENSITY_LABELS: Record<string, { zh: string; en: string }> = {
  slow: { zh: '缓慢', en: 'slow' },
  medium: { zh: '中等速度', en: 'medium-paced' },
  fast: { zh: '快速', en: 'fast' },
};

function describeCameraMovement(
  cameraMovement: string | null | undefined,
  intensity: string | null | undefined
): string | null {
  if (!cameraMovement) return null;
  if (cameraMovement === 'static') {
    const { zh, en } = CAMERA_LABELS.static;
    return `镜头运动：${zh}（${en}）。`;
  }
  const cam = CAMERA_LABELS[cameraMovement];
  if (!cam) return null;
  const itn = intensity ? INTENSITY_LABELS[intensity] : null;
  return itn
    ? `镜头运动：${itn.zh}${cam.zh}（${itn.en} ${cam.en}）。`
    : `镜头运动：${cam.zh}（${cam.en}）。`;
}

function describePosition(
  x: number,
  y: number,
  scale: number
): { zh: string; en: string } {
  const col =
    x < 0.33 ? ['左侧', 'left'] : x < 0.66 ? ['中央', 'center'] : ['右侧', 'right'];
  const row =
    y < 0.33 ? ['上方', 'top'] : y < 0.66 ? ['中部', 'middle'] : ['下方', 'bottom'];
  const depth =
    scale < 1.0
      ? ['背景', 'background']
      : scale < 1.4
        ? ['中景', 'middle-ground']
        : ['前景', 'foreground'];
  const zh = row[0] === '中部' ? `${depth[0]}${col[0]}` : `${depth[0]}${col[0]}（${row[0]}）`;
  const en = row[1] === 'middle' ? `${depth[1]} ${col[1]}` : `${depth[1]} ${col[1]} (${row[1]})`;
  return { zh, en };
}

export interface CharacterRefForPreview {
  id: string;
  name: string;
}

export interface PropRefForPreview {
  id: string;
  name: string;
}

export interface SceneRefForPreview {
  id: string;
  name: string;
}

export interface LayoutItemForPreview {
  characterId: string;
  x: number;
  y: number;
  scale: number;
}

/**
 * 把 @ 提及替换为「名字（参考图N）」。
 * - 长名字优先（避免「张三」吞掉「张三丰」的尾字）
 * - 同时处理 @[名字] 与 @名字 两种形式
 */
function replaceAtRef(text: string, name: string, refLabel: string): string {
  let next = text;
  next = next.split(`@[${name}]`).join(refLabel);
  next = next.split(`@${name}`).join(refLabel);
  return next;
}

/**
 * 前端预览图片 prompt（送给图片模型生成 keyframe 的版本）。
 *
 * 编号顺序：参考图1..N 角色 → 紧接 N+1..M 物品 → 末位 M+1（如有）场景
 */
export function buildKeyframePromptPreview(params: {
  userPrompt: string;
  characterRefs: CharacterRefForPreview[];
  propRefs?: PropRefForPreview[];
  sceneRef?: SceneRefForPreview | null;
  characterLayout?: LayoutItemForPreview[] | null;
  cameraMovement?: string | null;
  motionIntensity?: string | null;
}): string {
  const {
    userPrompt,
    characterRefs,
    propRefs = [],
    sceneRef = null,
    characterLayout,
    cameraMovement,
    motionIntensity,
  } = params;
  const trimmed = (userPrompt ?? '').trim();

  // 替换 @ → 名字（参考图N）
  // 顺序：长名字优先，避免短名前缀吞噬
  const allRefs: Array<{ name: string; label: string }> = [];
  characterRefs.forEach((ref, idx) => {
    allRefs.push({ name: ref.name, label: `${ref.name}（参考图${idx + 1}）` });
  });
  const propStartIndex = characterRefs.length + 1;
  propRefs.forEach((ref, idx) => {
    allRefs.push({
      name: ref.name,
      label: `${ref.name}（参考图${propStartIndex + idx}）`,
    });
  });
  if (sceneRef) {
    const sceneIdx = characterRefs.length + propRefs.length + 1;
    allRefs.push({
      name: sceneRef.name,
      label: `${sceneRef.name}（参考图${sceneIdx}）`,
    });
  }
  allRefs.sort((a, b) => b.name.length - a.name.length);

  let replaced = trimmed;
  for (const r of allRefs) {
    replaced = replaceAtRef(replaced, r.name, r.label);
  }

  const lines: string[] = [];
  if (characterRefs.length > 0) {
    const mapping = characterRefs
      .map((ref, idx) => `参考图${idx + 1}=${ref.name}`)
      .join('，');
    lines.push(`角色参考：${mapping}。`);
  }
  if (propRefs.length > 0) {
    const mapping = propRefs
      .map((ref, idx) => `参考图${propStartIndex + idx}=${ref.name}`)
      .join('，');
    lines.push(`物品参考：${mapping}。`);
  }
  if (sceneRef) {
    const sceneIdx = characterRefs.length + propRefs.length + 1;
    lines.push(`参考图${sceneIdx} 为场景设定图（${sceneRef.name}）。`);
  }

  // 构图描述
  if (characterLayout && characterLayout.length > 0) {
    const layoutById = new Map(characterLayout.map((it) => [it.characterId, it]));
    const composition: string[] = [];
    const compositionEn: string[] = [];
    for (const ref of characterRefs) {
      const it = layoutById.get(ref.id);
      if (!it) continue;
      const { zh, en } = describePosition(it.x, it.y, it.scale);
      composition.push(`${ref.name} 位于${zh}`);
      compositionEn.push(`${ref.name} at ${en}`);
    }
    if (composition.length > 0) {
      lines.push(`画面构图：${composition.join('，')}（${compositionEn.join('; ')}）。`);
    }
  }

  const cameraLine = describeCameraMovement(cameraMovement, motionIntensity);
  if (cameraLine) lines.push(cameraLine);
  if (replaced) lines.push(replaced);
  return lines.join('\n');
}

/**
 * 前端预览视频 prompt（送给视频模型激活的版本）。
 *
 * 不含构图描述（视频已用 keyframe 当首帧），不含"参考图N"术语，
 * 把 @ 提及统一还原为裸名字。
 */
export function buildVideoPromptPreview(params: {
  userPrompt: string;
  characterNames: string[];
  propNames?: string[];
  sceneName?: string | null;
  cameraMovement?: string | null;
  motionIntensity?: string | null;
}): string {
  const {
    userPrompt,
    characterNames,
    propNames = [],
    sceneName = null,
    cameraMovement,
    motionIntensity,
  } = params;
  const trimmed = (userPrompt ?? '').trim();

  const allNames = [
    ...characterNames,
    ...propNames,
    ...(sceneName ? [sceneName] : []),
  ];
  const sortedByLen = [...allNames].sort((a, b) => b.length - a.length);
  let replaced = trimmed;
  for (const name of sortedByLen) {
    replaced = replaceAtRef(replaced, name, name);
  }

  const lines: string[] = [];
  const cameraLine = describeCameraMovement(cameraMovement, motionIntensity);
  if (cameraLine) lines.push(cameraLine);
  if (replaced) lines.push(replaced);
  return lines.join('\n');
}
